import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { I18nProvider } from "@/contexts/I18nContext";
import { TenantProvider } from "@/contexts/TenantContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { BrandingInjector } from "@/components/BrandingInjector";
import Index from "./pages/Index";
const Analyze = lazy(() => import("./pages/Analyze"));

// Lazy load non-critical routes
const NotFound = lazy(() => import("./pages/NotFound"));
const Auth = lazy(() => import("./pages/Auth"));
const Unauthorized = lazy(() => import("./pages/Unauthorized"));
const Impersonate = lazy(() => import("./pages/Impersonate"));
const ClinicRoute = lazy(() => import("./pages/ClinicRoute"));
const ClinicSelector = lazy(() => import("./pages/ClinicSelector"));
const AdminLayout = lazy(() => import("./components/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const TenantRegister = lazy(() => import("./pages/admin/Register"));
const BrandingPage = lazy(() => import("./pages/admin/Branding"));
const UserManagement = lazy(() => import("./pages/admin/UserManagement"));
const Settings = lazy(() => import("./pages/admin/Settings"));
const Tracking = lazy(() => import("./pages/admin/Tracking"));
const PlatformDirectory = lazy(() => import("./pages/admin/PlatformDirectory"));
const GlobalDashboard = lazy(() => import("./pages/admin/GlobalDashboard"));
const PlatformSettings = lazy(() => import("./pages/admin/PlatformSettings"));
const AdGenerator = lazy(() => import("./pages/admin/AdGenerator"));
const Landing = lazy(() => import("./pages/Landing"));
const TermsOfService = lazy(() => import("./pages/legal/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/legal/PrivacyPolicy"));
const GDPRCompliance = lazy(() => import("./pages/legal/GDPRCompliance"));
const DataSecurity = lazy(() => import("./pages/legal/DataSecurity"));
const CookiePolicy = lazy(() => import("./pages/legal/CookiePolicy"));
const Contact = lazy(() => import("./pages/legal/Contact"));
const Disclaimer = lazy(() => import("./pages/legal/Disclaimer"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // Data stays fresh for 5 minutes
      gcTime: 10 * 60 * 1000, // Cache persists for 10 minutes (formerly cacheTime)
      refetchOnWindowFocus: false, // Don't refetch on window focus for speed
      refetchOnReconnect: false, // Don't refetch on reconnect
      retry: 1, // Only retry once on failure
    },
  },
});

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <I18nProvider>
          <AuthProvider>
            <TenantProvider>
              <BrandingInjector />
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <Suspense fallback={<div className="min-h-screen bg-gradient-subtle flex items-center justify-center"><div className="animate-pulse text-muted-foreground">Loading...</div></div>}>
                  <Routes>
                    <Route path="/" element={<Landing />} />
                    <Route path="/__impersonate" element={<Impersonate />} />
                    <Route path="/clinic/:subdomain" element={<ClinicRoute />} />
                    <Route path="/clinics" element={<ClinicSelector />} />
                    <Route path="/analyze" element={<Analyze />} />
                    <Route path="/analyze-old" element={<Index />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/unauthorized" element={<Unauthorized />} />
                    <Route path="/legal/terms" element={<TermsOfService />} />
                    <Route path="/legal/privacy" element={<PrivacyPolicy />} />
                    <Route path="/legal/gdpr" element={<GDPRCompliance />} />
                    <Route path="/legal/security" element={<DataSecurity />} />
                    <Route path="/legal/cookies" element={<CookiePolicy />} />
                    <Route path="/legal/contact" element={<Contact />} />
                    <Route path="/legal/disclaimer" element={<Disclaimer />} />
                    {/* Registration route - outside AdminLayout so users without tenant can access */}
                    <Route path="/admin/register" element={<TenantRegister />} />
                    <Route path="/admin" element={<AdminLayout />}>
                      <Route path="dashboard" element={<AdminDashboard />} />
                      <Route path="branding" element={<BrandingPage />} />
                      <Route path="ad-generator" element={<AdGenerator />} />
                      <Route path="users" element={<UserManagement />} />
                      <Route path="settings" element={<Settings />} />
                      <Route path="tracking" element={<Tracking />} />
                      <Route path="platform" element={<PlatformDirectory />} />
                      <Route path="global" element={<GlobalDashboard />} />
                      <Route path="platform-settings" element={<PlatformSettings />} />
                    </Route>
                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </BrowserRouter>
            </TenantProvider>
          </AuthProvider>
        </I18nProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
